import React, { useState, useEffect, useCallback } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { apiFetch } from '../api';
import { parseTermin, terminTime, notifyTermineChanged } from '../terminUtils';
import { IconCalendar, IconTrash } from './icons';

const TERMINE_API = '/api/termine';

// Header-Gradient (wie Navbar) — für den Terminieren-Button
const HEADER_GRADIENT = 'linear-gradient(90deg, #3b3be8, #a855f7)';

// Termin-Bereich in der Lead-Detailansicht: anlegen + bestehende Termine sehen
export default function TermineSection({ leadId, authHeader }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [datum, setDatum] = useState('');
  const [zeit, setZeit] = useState('');
  const [titel, setTitel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Termin, dessen Löschen noch bestätigt werden muss
  const [pendingRemove, setPendingRemove] = useState(null);

  const reload = useCallback(() => {
    apiFetch(`${TERMINE_API}/lead/${leadId}`, { headers: authHeader })
      .then(r => r.ok ? r.json() : [])
      .then(setList)
      .catch(() => {});
  }, [leadId, authHeader]);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    if (!datum || !zeit) { setError('Bitte Tag und Uhrzeit wählen.'); return; }
    setSaving(true); setError('');
    try {
      const res = await apiFetch(TERMINE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ lead_id: leadId, titel: titel || null, termin_at: `${datum}T${zeit}` }),
      });
      if (!res.ok) throw new Error(res.status);
      setDatum(''); setZeit(''); setTitel(''); setOpen(false);
      reload();
      notifyTermineChanged();
    } catch {
      setError('Termin konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async id => {
    try {
      const res = await apiFetch(`${TERMINE_API}/${id}`, { method: 'DELETE', headers: authHeader });
      if (res.ok) {
        setList(prev => prev.filter(t => t.id !== id));
        notifyTermineChanged();
      }
    } catch {}
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: HEADER_GRADIENT, color: 'white', border: 'none', borderRadius: 10,
          padding: '12px 16px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          minHeight: 44, boxShadow: '0 1px 6px rgba(59,59,232,0.3)',
        }}
      >
        <IconCalendar /> Terminieren
      </button>

      {open && (
        <div style={{ marginTop: 10, padding: 12, background: '#f9f8ff', border: '1px solid #ede9fe', borderRadius: 10 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div className="form-field" style={{ flex: '1 1 140px', margin: 0 }}>
              <label>Tag</label>
              <input type="date" value={datum} onChange={e => setDatum(e.target.value)} />
            </div>
            <div className="form-field" style={{ flex: '1 1 110px', margin: 0 }}>
              <label>Uhrzeit</label>
              <input type="time" value={zeit} onChange={e => setZeit(e.target.value)} />
            </div>
          </div>
          <div className="form-field" style={{ marginTop: 10, marginBottom: 0 }}>
            <label>Titel (optional)</label>
            <input type="text" value={titel} onChange={e => setTitel(e.target.value)} placeholder="z. B. Rückruf, Vor-Ort-Termin" />
          </div>
          {error && <div className="login-error" role="alert" style={{ marginTop: 10 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={() => { setOpen(false); setError(''); }}>Abbrechen</button>
            <button type="button" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} onClick={save} disabled={saving} aria-busy={saving}>
              {saving ? 'Speichern...' : 'Termin speichern'}
            </button>
          </div>
        </div>
      )}

      {list.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map(t => {
            const d = parseTermin(t.termin_at);
            const past = d && d < new Date();
            return (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                background: 'white', border: '1px solid #ede9fe', opacity: past ? 0.6 : 1,
              }}>
                <div style={{
                  flexShrink: 0, textAlign: 'center', minWidth: 52, lineHeight: 1.2,
                  color: '#5b21b6', fontWeight: 700,
                }}>
                  <div style={{ fontSize: 11 }}>{d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '—'}</div>
                  <div style={{ fontSize: 14 }}>{d ? terminTime(d) : ''}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#1e1b4b' }}>
                  {t.titel || 'Termin'}
                  {past && <span style={{ color: '#9ca3af', fontSize: 11 }}> · vergangen</span>}
                </div>
                <button
                  type="button" onClick={() => setPendingRemove(t)} aria-label="Termin löschen"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 6, lineHeight: 0 }}
                >
                  <IconTrash />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {pendingRemove && (() => {
        const d = parseTermin(pendingRemove.termin_at);
        const wann = d ? ` am ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} um ${terminTime(d)} Uhr` : '';
        return (
          <ConfirmDialog
            title="Termin löschen?"
            message={`„${pendingRemove.titel || 'Termin'}“${wann} wird gelöscht.`}
            onConfirm={() => { remove(pendingRemove.id); setPendingRemove(null); }}
            onCancel={() => setPendingRemove(null)}
          />
        );
      })()}
    </div>
  );
}
